import { useCallback, useEffect, useState } from 'react';
import { Select } from 'antd';
import debug from 'debug';
import { uniq } from 'lodash-es';

import ItemTypeIcon from 'components/common/item-type-icon';
import { classnames } from 'lib/classnames';
import fetch from 'lib/fetch';
import useI18n from 'lib/hooks/useI18n';
import { ResultType, WorkflowDataProps } from 'lib/types/workflow';

import WorkspaceSelect from './WorkspaceSelect';

import styles from './index.module.less';

const cx = classnames(styles);
export type PluginsType = {
  man_hour_plugin?: string[]; // 工时
} & Record<string, string[]>;

interface SelectWorkspaceAndItemTypeProps {
  itemId: string;
  workspaceId: string;
  title?: string;
  onChange?: (changeData: {
    workspaceData?: Parse.Attributes;
    itemTypeData?: { key: string; value: string };
    securityLevelIsChange?: boolean;
  }) => void;
  onWorkflowChange?: (workspaceData: WorkflowDataProps & Parse.Attributes) => void;
  updateSecurityChange?: (result: boolean) => void;
  updateWorkflowPlugin?: (pluginsType: PluginsType) => void;
  itemChildren: Parse.Attributes[];
  itemParent: Parse.Attributes;
  updateErrorMessage: (result: ResultType) => void;
  currentItemTypeId?: string;
}

const log = debug('selectWorkspaceAndItemType:content');

const DEFAULT_FLOW_MESSAGE: ResultType = { result: false, message: '' };
const SelectWorkspaceAndItemType: React.FC<SelectWorkspaceAndItemTypeProps> = ({
  workspaceId,
  title,
  itemParent,
  itemChildren,
  onChange,
  onWorkflowChange,
  updateWorkflowPlugin,
  updateErrorMessage,
  currentItemTypeId,
}) => {
  const { t } = useI18n();
  const [workspacePlugins, setWorkspacePlugins] = useState<Record<string, PluginsType>>({});
  const [itemTypeList, setItemTypeList] = useState([]);
  const [selectItemType, setSelectItemType] = useState();
  const [selectLoading, setSelectLoading] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(undefined); // 当前迁移事项的空间信息
  const [selectDetail, setSelectDetail] = useState(undefined); // 当前选择事项的空间信息

  // 判断当前迁移是否符合层级关系，在空间切换和类型切换调用
  const checkItemHierarchy = useCallback(
    ({ itemTypeValue, workspaceDetail }) => {
      updateErrorMessage(DEFAULT_FLOW_MESSAGE);
      const selectItemTypeValue = itemTypeValue || selectItemType; // 迁移的目标事项类型
      const selectWorkspace = workspaceDetail || selectDetail; // 迁移的目标空间
      log('current select hierarchy', workspaceId, selectItemTypeValue, selectWorkspace);
      if (!selectItemTypeValue || !selectWorkspace) return;

      // 查找出不包含工时类型的子事项
      // 这个应该看当前迁移出的事项的空间
      const manHourItemTypes = workspacePlugins[workspaceId]?.man_hour_plugin || [];
      const items = itemChildren.filter(item => !manHourItemTypes.includes(item.itemType.key));
      // 查找出子事项是否是同空间还是不同空间
      const sameWorkspaceItems = items.filter(item => item.workspace.objectId === workspaceId);
      log('same workspace children', sameWorkspaceItems);
      // 如果不相等，说明是迁移空间
      if (workspaceId !== selectWorkspace.objectId) {
        if (sameWorkspaceItems.length) {
          log('condition', workspaceId, selectWorkspace.objectId, sameWorkspaceItems, itemChildren);
          // 如果存在子事项是当前空间的，则不允许迁移
          updateErrorMessage({ result: false, message: t('pages.itemMove.hierarchyTip1') });
          return;
        }
      }

      // 检查层级
      const checkHierarchy = (
        selectWorkspaceDetail: Parse.Attributes,
        isParent: boolean,
        children?: Parse.Attributes[],
      ): boolean => {
        const checkValue = isParent ? { parent: itemParent } : { children };
        const matchChildrenHierarchy = checkItemTypeHierarchy(selectWorkspaceDetail, selectItemTypeValue, checkValue);
        if (!matchChildrenHierarchy) {
          log('check result', checkValue, selectWorkspaceDetail, selectItemTypeValue);
          updateErrorMessage({
            result: false,
            message: isParent ? t(`pages.itemMove.hierarchyTip3`) : t(`pages.itemMove.hierarchyTip2`),
          });
          return false;
        }
        return true;
      };

      // 如果相等，说明是在同一空间内修改类型
      // 如果当前事项有同空间的子事项，需要判断层级管理
      if (sameWorkspaceItems.length && !checkHierarchy(currentDetail, false, sameWorkspaceItems)) {
        return;
      }
      // 如果当前事项有同空间的父事项，需要判断层级管理
      if (
        itemParent &&
        itemParent.workspace.objectId === workspaceId &&
        workspaceId === selectWorkspace.objectId &&
        !checkHierarchy(selectWorkspace, true)
      ) {
        return;
      }

      // 如果是不同空间，迁移目前的父/子事项都在迁移空间
      if (workspaceId !== selectWorkspace.objectId && inWorkspace(selectWorkspace.objectId, itemParent, itemChildren)) {
        log('not equal workspace condition', workspaceId, selectWorkspace.objectId);
        if (itemParent && !checkHierarchy(selectWorkspace.objectId, true)) {
          return;
        }
        if (itemChildren?.length && !checkHierarchy(selectWorkspace, false, itemChildren)) {
          return;
        }
      }
    },
    [
      updateErrorMessage,
      selectItemType,
      selectDetail,
      workspaceId,
      workspacePlugins,
      itemChildren,
      currentDetail,
      itemParent,
      t,
    ],
  );

  const fetchWorkflow = useCallback(
    async (workspaceId: string, itemTypeId: string, workflowSchemeId: string) => {
      log('fetch workflow', workflowSchemeId, itemTypeId, workspaceId);
      if (!workflowSchemeId || !itemTypeId || !workspaceId) return;
      try {
        setSelectLoading(true);
        const workflowData = await fetch.$post(`/workflows/workspace/itemType`, {
          itemType: itemTypeId,
          workflowScheme: workflowSchemeId,
          workspace: workspaceId,
        });
        onWorkflowChange?.(workflowData);
      } catch (error) {
        console.error(error.message);
      } finally {
        setSelectLoading(false);
      }
    },
    [onWorkflowChange],
  );

  const itemTypeChange = useCallback(
    async (value, data) => {
      console.info('itemTypeChange', value, data);
      log('itemType change', selectDetail, value, data);
      await fetchWorkflow(selectDetail.objectId, value, selectDetail.workflowScheme?.objectId);
      setSelectItemType(value);
      checkItemHierarchy({ itemTypeValue: value, workspaceDetail: undefined });
      onChange({ itemTypeData: data });
    },
    [selectDetail, fetchWorkflow, checkItemHierarchy, onChange],
  );

  // 查询空间的插件
  const fetchWorkspacePlugin = useCallback(
    async ({ itemTypes, workspaceData }) => {
      const itemTypeData = itemTypes || itemTypeList;
      const selectWorkspaceId = workspaceData.objectId;

      log('fetch workspace plugin', workspaceId, selectWorkspaceId, itemTypeData);
      if (!selectWorkspaceId) return;
      try {
        setSelectLoading(true);
        // 查询当前空间可用插件的类型
        const data = await fetch.$get(`/itemType/apps/workspace/${workspaceData.key}`);
        log('workspace plugin data', data, itemTypeList);
        workspacePlugins[selectWorkspaceId] = data;
        setWorkspacePlugins(workspacePlugins);
        if (selectWorkspaceId === workspaceId) {
          updateWorkflowPlugin && updateWorkflowPlugin(data);
        }
      } catch (error) {
        console.error(error.message);
      } finally {
        setSelectLoading(false);
      }
    },
    [itemTypeList, updateWorkflowPlugin, workspaceId, workspacePlugins],
  );

  const workspaceChange = useCallback(
    async (data: Parse.Attributes, currentWorkspaceDetail?: Parse.Attributes) => {
      log('workspace change', currentDetail, currentWorkspaceDetail, selectDetail, data);
      if (!data?.objectId) return;
      setSelectDetail(data);
      // 只能选第一层的条件限制
      // 1、如果是不同空间的迁移，并且当前事项有同空间的父事项，这种情况只能选择目标空间的第一层
      // 2、如果是相同空间的迁移，并且有父事项和当前空间不一样
      let isFirstHierarchy = true;
      if (data.objectId === workspaceId) {
        if (itemParent && itemParent.workspace.objectId === workspaceId) {
          isFirstHierarchy = false;
        }
      } else {
        // 如果是不同空间，判断一下原来事项的父子事项是否和迁移目标空间是否一致
        if (inWorkspace(data.objectId, itemParent, itemChildren)) {
          // 这种情况可以选择，目标空间的子级类型，但需要判断层级关系
          isFirstHierarchy = false;
        }
      }
      const itemTypeList = await getItemTypeList(data, isFirstHierarchy);
      setItemTypeList(itemTypeList);
      let changeItemType = null;
      if (!itemTypeList.find(itemType => itemType.value === selectItemType)) {
        setSelectItemType(undefined);
        changeItemType = { key: '', value: '' };
      }
      await fetchWorkflow(data.objectId, selectItemType, data.workflowScheme?.objectId);
      await fetchWorkspacePlugin({ workspaceData: data, itemTypes: itemTypeList });
      const current = currentWorkspaceDetail || currentDetail;
      onChange({
        workspaceData: data,
        itemTypeData: changeItemType,
        securityLevelIsChange: data.securityLevelScheme?.objectId !== current.securityLevelScheme?.objectId,
      });
      // 检查层级处理
      checkItemHierarchy({ workspaceDetail: data, itemTypeValue: undefined });
    },
    [
      currentDetail,
      selectDetail,
      workspaceId,
      fetchWorkflow,
      selectItemType,
      fetchWorkspacePlugin,
      onChange,
      checkItemHierarchy,
      itemParent,
      itemChildren,
    ],
  );

  const onLoad = useCallback(
    data => {
      setCurrentDetail(data);
      workspaceChange(data, data);
    },
    [workspaceChange],
  );

  useEffect(() => {
    if (currentItemTypeId) {
      const _data = itemTypeList.find(item => item.value === currentItemTypeId);
      if (_data) {
        itemTypeChange(currentItemTypeId, _data);
      } else {
        setSelectItemType(undefined);
      }
    }
  }, [currentItemTypeId, itemTypeChange, itemTypeList]);

  return (
    <div className={cx('select-wrap')}>
      {title && <div className={cx('title')}>{title}</div>}
      <div className={cx('select-item')}>
        <div className={cx('select-label')}>{t('pages.itemMove.targetWorkspace')}</div>
        <WorkspaceSelect initial={workspaceId} onLoad={onLoad} onWorkspaceChange={workspaceChange} />
      </div>
      <div className={cx('select-item')}>
        <div className={cx('select-label')}>{t('pages.itemMove.targetItemType')}</div>
        <Select
          loading={selectLoading}
          className={cx('select-item-content')}
          showSearch
          filterOption={(input, option) => {
            const value = (option?.children?.[1] || '').trim().toLowerCase();
            return value.includes(input.trim().toLowerCase());
          }}
          placeholder={t('pages.itemMove.selectItemType')}
          value={selectItemType}
          onChange={itemTypeChange}
          disabled={!!currentItemTypeId}
        >
          {itemTypeList.map(ele => {
            return (
              <Select.Option key={ele.label} value={ele.value}>
                <ItemTypeIcon name={ele.name} icon={ele.icon} className={cx('item-type-icon')} />
                {ele.label}
              </Select.Option>
            );
          })}
        </Select>
      </div>
    </div>
  );
};

function mapList(list, result = []) {
  for (const item of list) {
    const { objectId, children } = item;
    if (children) {
      result = mapList(children, result);
    }
    if (!result.find(item => item.value === objectId)) {
      result.push(objectId);
    }
  }
  return result;
}

// 将事项层级的树结构改成列表
export async function getItemTypeList(workspaceData: Parse.Attributes, isFirstHierarchy = false) {
  let result = [];
  try {
    log('get item type by workspace scheme', workspaceData, isFirstHierarchy);
    const currentItemTypes = workspaceData.itemTypeScheme?.hierarchy;
    const itemTypes = JSON.parse(currentItemTypes);
    if (isFirstHierarchy) {
      result = itemTypes.map(type => type.objectId);
    } else {
      result = mapList(itemTypes);
    }
    log('item type data, get types', result);
    const list = await fetch.$post('/itemTypes/list', {
      workspace: workspaceData.objectId,
      currentWorkspace: workspaceData.objectId,
      displayModule: 'view',
      childCardDepsItemTypeIds: result,
    });
    result = list.results?.map(itemType => ({
      label: itemType.name,
      key: itemType.key,
      value: itemType.objectId,
      icon: itemType.icon,
    }));

    log('new item types', result);
  } catch (error) {
    console.error('get item type error', error.message);
  }
  return result;
}

// 判断当前事项的父子事项是否符合层级关系
export function checkItemTypeHierarchy(
  workspaceData: Parse.Attributes,
  currentItemType: string,
  { children, parent }: { children?: Parse.Attributes[]; parent?: Parse.Attributes },
): boolean {
  let result = false;
  try {
    log('check item type schemes', workspaceData, currentItemType, children, parent);
    const currentItemTypes = workspaceData.itemTypeScheme?.hierarchy;
    const itemTypes = JSON.parse(currentItemTypes);

    type ItemTypeMap = {
      objectId: string;
      children: Array<ItemTypeMap>;
    };
    // 获取目标事项的所有子事项类型
    const getChildrenType = (type: string, list: Array<ItemTypeMap>): string[] => {
      let result = [];
      for (const itemType of list) {
        if (itemType.children?.length) {
          result = result.concat(getChildrenType(type, itemType.children));
          if (itemType.objectId === type) {
            result = result.concat(itemType.children.map(child => child.objectId));
          }
        }
      }
      return uniq(result);
    };

    // 获取目标事项的所有父事项类型
    const getParentType = (type: string, list: Array<ItemTypeMap>, parentId?: string): string[] => {
      let result = [];
      for (const itemType of list) {
        if (itemType.children?.length) {
          result = result.concat(getParentType(type, itemType.children, itemType.objectId));
        }
        if (parentId && itemType.objectId === type) {
          result.push(parentId);
        }
      }
      return uniq(result);
    };

    if (children) {
      const childrenItemTypeIds = uniq(children.map(child => child.itemType.objectId));
      const targetChildren = getChildrenType(currentItemType, itemTypes);
      result = childrenItemTypeIds.every(itemTypeId => targetChildren.includes(itemTypeId));
    }
    if (parent) {
      const targetParent = getParentType(currentItemType, itemTypes);
      return targetParent.includes(parent.itemType.objectId);
    }
    log('item type schemes', result);
  } catch (error) {
    console.error(error.message);
  }
  return result;
}

// 查询父子事项是否和迁移空间为同一空间
export function inWorkspace(
  workspaceId: string,
  itemParent?: Parse.Attributes,
  itemChildren?: Parse.Attributes[],
): boolean {
  // 父子事项必须有一项存在
  if (!itemParent && !itemChildren?.length) return false;
  if (itemParent && itemParent.workspace.objectId !== workspaceId) {
    return false;
  }
  if (itemChildren?.length && itemChildren.some(child => child.workspace.objectId !== workspaceId)) {
    return false;
  }
  return true;
}

export default SelectWorkspaceAndItemType;
